import { Link } from 'react-router-dom';
import { Card, Badge } from '@rackd/ui';
import {
  IconClock,
  IconCalendar,
  IconBarbell,
  IconUser,
  IconChevronRight,
} from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';
import type { ProgramSubmission } from '../api';

interface SubmissionCardProps {
  submission: ProgramSubmission;
}

const levelColors = {
  beginner: 'bg-[var(--success)]/10 text-[var(--success)]',
  intermediate: 'bg-[var(--warning)]/10 text-[var(--warning)]',
  advanced: 'bg-[var(--error)]/10 text-[var(--error)]',
};

const statusColors = {
  pending: 'bg-[var(--warning)]/10 text-[var(--warning)]',
  approved: 'bg-[var(--success)]/10 text-[var(--success)]',
  rejected: 'bg-[var(--error)]/10 text-[var(--error)]',
};

export function SubmissionCard({ submission }: SubmissionCardProps) {
  return (
    <Link to={`/reviews/${submission.id}`}>
      <Card className="p-4 hover:border-[var(--accent-primary)]/30 transition-colors">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-[var(--text)] truncate">
                {submission.name}
              </h3>
              <Badge className={levelColors[submission.level]}>
                {submission.level}
              </Badge>
              <Badge className={statusColors[submission.status]}>
                {submission.status}
              </Badge>
            </div>

            {/* Description */}
            {submission.description && (
              <p className="text-sm text-[var(--text-tertiary)] line-clamp-2 mb-3">
                {submission.description}
              </p>
            )}

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--text-tertiary)]">
              <span className="flex items-center gap-1">
                <IconUser size={14} />
                {submission.authorName}
              </span>
              <span className="flex items-center gap-1">
                <IconCalendar size={14} />
                {submission.durationWeeks} weeks
              </span>
              <span className="flex items-center gap-1">
                <IconBarbell size={14} />
                {submission.daysPerWeek} days/week
              </span>
              <span className="flex items-center gap-1">
                <IconClock size={14} />
                {formatDistanceToNow(submission.submittedAt, { addSuffix: true })}
              </span>
            </div>

            {/* Focus */}
            <div className="mt-2">
              <span className="inline-block px-2 py-0.5 bg-[var(--background-tertiary)] rounded text-xs text-[var(--text-secondary)]">
                {submission.focus}
              </span>
            </div>
          </div>

          {/* Arrow */}
          <IconChevronRight size={20} className="text-[var(--text-tertiary)] flex-shrink-0" />
        </div>
      </Card>
    </Link>
  );
}
